import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import Swal from "sweetalert2";
import { fetchParameters, fetchRestructuringQuotaValue, restructureCredit } from "../gestionCarteraApi";
import { formatMoney, isMoneyAmount } from "../gestionCarteraFormat";
import { COLORS, labelSx, ModalShell, PrimaryButton, SecondaryButton } from "../gestionCarteraUi";

const MAX_RESTRUCTURE_QUOTAS = 24;
const QUOTA_OPTIONS = Array.from({ length: MAX_RESTRUCTURE_QUOTAS }, (_, index) => index + 1);
const ALLOWED_CLIENT_STATUSES = ["COMPLETED", "REESTRUCTURADO", "AMPLIADO", "APPROVED"];

/** Mismas validaciones que el antiguo detalle de Cobranza, aplicadas al crédito que se está viendo. */
export const getRestructureBlocker = (credit, clientCredits) => {
  if (clientCredits.some((item) => !ALLOWED_CLIENT_STATUSES.includes(item.status))) {
    return "El cliente ya tiene una solicitud de reestructuración en curso.";
  }
  if (credit.status !== "APPROVED") return "Solo se pueden reestructurar créditos en estado aprobado.";
  if (credit.reestructurado) return "No está permitido reestructurar un crédito que ya es una reestructuración.";
  return null;
};

export default function ReestructurarModal({ open, credit, balanceCredit, clientCredits, onClose, onDone }) {
  const [parameters, setParameters] = useState(null);
  const [numberQuotas, setNumberQuotas] = useState("");
  const [quotaValue, setQuotaValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const blocker = credit ? getRestructureBlocker(credit, clientCredits) : null;
  const hasBalance = isMoneyAmount(balanceCredit);

  useEffect(() => {
    if (!open) return;
    setNumberQuotas("");
    setQuotaValue(null);
    fetchParameters().then(setParameters).catch(() => setParameters(null));
  }, [open]);

  useEffect(() => {
    if (!numberQuotas || !parameters || !hasBalance) return;
    setQuotaValue(null);
    fetchRestructuringQuotaValue({
      creditValue: Number(balanceCredit),
      numberQuotas,
      commerceId: credit.commerce?.oldId,
      tarifa: 0,
      svd: parameters.seguroVidaDeudor,
      interest: parameters.interestRate,
    })
      .then(setQuotaValue)
      .catch(() => setQuotaValue(null));
  }, [numberQuotas, parameters, credit, balanceCredit, hasBalance]);

  const handleSubmit = async () => {
    if (!hasBalance) return;
    setSaving(true);
    try {
      await restructureCredit({
        creditUuid: credit.uuid,
        valorAcuerdoPago: quotaValue,
        valorCreditoReestructurado: Number(balanceCredit),
        quotasReestructurado: numberQuotas,
        interestReestructurado: parameters.interestRate,
        svdReestructurado: parameters.seguroVidaDeudor,
        fgaReestructurado: 0,
      });
      setSaving(false);
      onDone();
      Swal.fire({ icon: "success", title: "Crédito reestructurado", text: "Se reestructuró exitosamente el crédito.", confirmButtonColor: COLORS.primary });
    } catch (error) {
      setSaving(false);
      Swal.fire({ icon: "error", title: "Error", text: "Error en la reestructuración del crédito.", confirmButtonColor: COLORS.primary });
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={saving ? undefined : onClose}
      title="Reestructurar crédito"
      subtitle={credit ? `Crédito ${credit.oldId}` : ""}
      actions={
        <>
          <SecondaryButton onClick={onClose} disabled={saving}>Cancelar</SecondaryButton>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={Boolean(blocker) || !hasBalance || saving || !quotaValue}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Reestructurar
          </PrimaryButton>
        </>
      }
    >
      {blocker ? (
        <Alert severity="warning">{blocker}</Alert>
      ) : !hasBalance ? (
        <Alert severity="warning">Este crédito no tiene saldo total, así que no se puede reestructurar.</Alert>
      ) : (
        <>
          <Box>
            <Typography sx={labelSx}>Deuda total</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.primary }}>{formatMoney(balanceCredit)}</Typography>
          </Box>
          <TextField select label="Número de cuotas" value={numberQuotas} onChange={(event) => setNumberQuotas(event.target.value)} disabled={!parameters}>
            {QUOTA_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <Box>
            <Typography sx={labelSx}>Nuevo valor de cuota</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {numberQuotas && !quotaValue ? <CircularProgress size={18} /> : formatMoney(quotaValue)}
            </Typography>
          </Box>
        </>
      )}
    </ModalShell>
  );
}
